import { RouteComponentProps } from '@reach/router';
import { ResourcesApi } from 'api/resourcesApi';
import EqualHeightsList from 'components/resources/EqualHeightsList';
import ResourceBankPage from 'components/resources/ResourceBankPage';
import {
  ResourceCards,
  ResourceTypeTabs,
} from 'components/resources/ResourceList';
import ResourcePageStatus from 'components/resources/ResourcePageStatus';
import {
  awesomeStuffPath,
  byName,
  categoryPath,
  isPageData,
  resourceTypeTabs,
  ResourcesRouteParams,
  toId,
  usePageTitle,
  useResourcesPage,
} from 'components/resources/resourcesHelpers';
import ResourceTile from 'components/resources/ResourceTile';
import SubjectSiteLayout from 'components/subjectSite/SubjectSiteLayout';
import React, { useRef } from 'react';

type Props = RouteComponentProps<ResourcesRouteParams>;

const ResourceCategoryPage: React.FC<Props> = ({
  subject: subjectCode,
  id,
  type,
}) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const resourceCategoriesId = toId(id);
  const result = useResourcesPage(
    subjectCode,
    resourceCategoriesId === null
      ? null
      : (subjectsId) =>
          ResourcesApi.getCategory(subjectsId, resourceCategoriesId),
    id ?? ''
  );
  usePageTitle(
    isPageData(result) ? `${result.subject.name} - Resource Bank` : null
  );

  if (!isPageData(result)) return <ResourcePageStatus result={result} />;

  const { subject, details: category } = result;
  const code = subject.internalCode;
  const children = byName(category.childResourceCategories);
  const { tabs, activeTab } = resourceTypeTabs(category.resources, type, true);
  const resources = byName(
    category.resources.filter(
      (resource) => resource.resourceType === activeTab?.resourceType
    )
  );
  const bHasContent = tabs.length > 0 || children.length > 0;

  return (
    <SubjectSiteLayout colorCss={category.colorCSS}>
      <ResourceBankPage
        ref={pageRef}
        subjectCode={code}
        title={category.name}
        subtitle={category.description}
        imageSrc={category.imagePath}
        backPath={
          category.parentResourceCategoriesId
            ? categoryPath(code, category.parentResourceCategoriesId)
            : awesomeStuffPath(code)
        }
      >
        <ResourceTypeTabs
          tabs={tabs}
          activeTab={activeTab}
          basePath={categoryPath(code, category.resourceCategoriesId)}
        />
        {bHasContent ? (
          <>
            <ResourceCards
              subjectCode={code}
              subjectsId={subject.subjectsID}
              resources={resources}
              colorCSS={category.colorCSS}
              pageRef={pageRef}
              bCentred
            />
            <div className="LineSpacer6" />
            <div className="VirtualPECupboard">
              <EqualHeightsList
                className="WarpperActivity WrapperList01"
                cardsSource={category}
                pageRef={pageRef}
              >
                {children.map((child) => (
                  <React.Fragment key={child.resourceCategoriesId}>
                    {' '}
                    <ResourceTile
                      className="Item ItemList01 FixedColour"
                      colorCSS={child.colorCSS}
                      name={child.name}
                      imageSrc={child.imagePath}
                      imageAlt={child.name}
                      to={categoryPath(code, child.resourceCategoriesId)}
                    />
                  </React.Fragment>
                ))}
              </EqualHeightsList>
            </div>
          </>
        ) : (
          <p>No resources found.</p>
        )}
      </ResourceBankPage>
    </SubjectSiteLayout>
  );
};

export default ResourceCategoryPage;
